import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { PRIVACY_URL, TERMS_URL } from '@/config/legal';
import { setPixelUser } from '../analytics/metaPixel';
import * as auth from '../api/auth';

/**
 * Who is signed in, owned by the web client.
 *
 * Replaces the shared AuthContext, which reached for RevenueCat and the mobile analytics
 * buffer on every sign-in. The surface is deliberately the same so screens change by one
 * import, and `isInitializing` is kept because guarding on the wrong flag is what logged
 * people out on reload: the session is restored asynchronously, and a guard that treats
 * "not yet known" as "signed out" redirects someone who is perfectly signed in.
 */
export interface Session {
  user: auth.SessionUser | null;
  isAuthenticated: boolean;
  /** The one-time session bootstrap. Guards must WAIT on this, never redirect during it. */
  isInitializing: boolean;
  error: string | null;
  signUp: (email: string, password: string, opts: { acceptedTerms: boolean }) => Promise<void>;
  confirmSignUp: (email: string, code: string) => Promise<void>;
  resendConfirmation: (email: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  /*
   * No `deleteAccount` here, on purpose. It used to close the Cognito login and nothing else,
   * never `DELETE /me/account`, which leaves the person's data on the server with no login that
   * can ever reach it. Deleting an account is web/src/screens/account/deleteAccount.ts, which
   * purges the data first in the phone's order.
   */
  forgotPassword: (email: string) => Promise<void>;
  confirmForgotPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  clearError: () => void;
}

export const SessionContext = createContext<Session | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUserState] = useState<auth.SessionUser | null>(null);
  const [isInitializing, setIsInitializing] = useState(auth.authConfigured());
  const [error, setError] = useState<string | null>(null);

  /*
   * Every change of who is signed in goes through here, so the ad pixel hears it at the same
   * moment — not a render later. Account signs out with signOut().then(() => navigate('/')),
   * and fbevents sends a PageView of its own on that pushState: told from an effect, the pixel
   * would still be naming the previous person when it went (analytics/metaPixel.ts).
   */
  const setUser = useCallback((next: auth.SessionUser | null) => {
    setUserState(next);
    void setPixelUser(next);
  }, []);

  useEffect(() => {
    if (!auth.authConfigured()) {
      // No pool configured: nobody is signed in, and nothing is pending.
      setIsInitializing(false);
      return;
    }
    let live = true;
    void auth
      .currentUser()
      .then((u) => {
        if (live) setUser(u);
      })
      .catch(() => {
        if (live) setUser(null);
      })
      .finally(() => {
        if (live) setIsInitializing(false);
      });
    return () => {
      live = false;
    };
  }, [setUser]);

  /** Every action reports its own failure and re-throws, so screens can react as well. */
  const run = useCallback(async <T,>(action: () => Promise<T>): Promise<T> => {
    setError(null);
    try {
      return await action();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong.');
      throw e;
    }
  }, []);

  const value = useMemo<Session>(
    () => ({
      user,
      isAuthenticated: user !== null,
      isInitializing,
      error,
      clearError: () => setError(null),
      signUp: (email, password, opts) =>
        run(async () => {
          if (!opts.acceptedTerms) {
            throw new Error(`Please accept the Terms (${TERMS_URL}) and Privacy Policy (${PRIVACY_URL}).`);
          }
          // The EMAIL is the username: the pool's UsernameAttributes demands it, and a
          // generated one throws InvalidParameterException.
          await auth.signUp(email, password, { acceptedTermsAt: new Date().toISOString() });
        }),
      confirmSignUp: (email, code) => run(() => auth.confirmSignUp(email, code)),
      resendConfirmation: (email) => run(() => auth.resendConfirmation(email)),
      signIn: (email, password) =>
        run(async () => {
          setUser(await auth.signIn(email, password));
        }),
      signOut: () =>
        run(async () => {
          await auth.signOut();
          setUser(null);
        }),

      forgotPassword: (email) => run(() => auth.forgotPassword(email)),
      confirmForgotPassword: (email, code, newPassword) =>
        run(() => auth.confirmForgotPassword(email, code, newPassword)),
    }),
    [user, isInitializing, error, run, setUser],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
